import {Button, Dialog, DotProgress} from '@wandb/components';
import {SensitiveInfoWrapper} from '@wandb/weave/components/SensitiveInfoWrapper';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import {toCanvas} from 'qrcode';
import React from 'react';

import {backendHost} from '../config';
import {useGenerateApiKeyMutation} from '../generated/graphql';
import {useIsApiKeyCreationDisabled} from '../pages/OrgDashboard/PrivacySettings/getOrganizationPrivacySettings';
import {propagateErrorsContext} from '../util/errors';

interface MobileLoginModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function mobileLoginPayload(apiKey: string): string {
  const payload = JSON.stringify({
    apiKey,
    server: backendHost(),
  });

  // Encode to UTF-8 bytes first: btoa alone throws on non-Latin-1 characters.
  const bytes = new TextEncoder().encode(payload);
  return btoa(String.fromCharCode(...bytes));
}

const MobileLoginQrCode: React.FC = () => {
  const [generateApiKey, {data, loading, error, called}] =
    useGenerateApiKeyMutation({
      // Failures are surfaced to the user through the `error` state below.
      // The context suppresses the global error dialog; the no-op `onError`
      // makes the mutate promise resolve instead of rejecting unhandled.
      context: propagateErrorsContext(),
      onError: () => undefined,
    });

  const apiKey = data?.generateApiKey?.secretApiKey;
  const isApiKeyCreationDisabled = useIsApiKeyCreationDisabled();
  const errorMessage = isApiKeyCreationDisabled
    ? 'Could not generate an API key. An org admin has disabled API key creation.'
    : 'Could not generate an API key. Please try again.';

  return (
    <Tailwind>
      {/* Fixed at the QR canvas's 200px height so swapping between the
          button, loader, error, and canvas doesn't shift the layout. */}
      <div className="flex h-[200px] items-center justify-center">
        {(() => {
          if (error) {
            return <p>{errorMessage}</p>;
          } else if (apiKey) {
            return (
              <SensitiveInfoWrapper>
                <canvas
                  ref={canvas => {
                    if (canvas) {
                      toCanvas(canvas, mobileLoginPayload(apiKey), {
                        width: 200,
                      });
                    }
                  }}
                />
              </SensitiveInfoWrapper>
            );
          } else if (loading || called) {
            return <DotProgress aria-label="Generating API key" />;
          } else {
            return (
              <Button
                onClick={() =>
                  generateApiKey({variables: {description: 'W&B iOS'}})
                }>
                Generate login code
              </Button>
            );
          }
        })()}
      </div>
    </Tailwind>
  );
};

export const MobileLoginModal: React.FC<MobileLoginModalProps> = ({
  isOpen,
  onClose,
}) => {
  return (
    <Dialog.Modal isOpen={isOpen}>
      <Dialog.Heading>Log in on mobile</Dialog.Heading>
      <Dialog.Content>
        Open the W&amp;B iOS app, tap &ldquo;Dedicated Cloud log in&rdquo;, and
        scan this code. Do not screenshot or save the code.
        {isOpen && <MobileLoginQrCode />}
      </Dialog.Content>
      <Dialog.Footer>
        <Button onClick={onClose}>Done</Button>
      </Dialog.Footer>
    </Dialog.Modal>
  );
};
